import { Navigate, Outlet, useLocation } from "react-router";

import { type Access, canAccess } from "@/auth/access";
import { useMe } from "@/auth/session";
import { SessionProvider, useSession } from "@/auth/session-context";
import { ErrorState } from "@/components/states";
import { ForbiddenPage } from "@/pages/error-pages";
import { FullScreenLoader } from "@punto-firma/ui/components/full-screen-loader";

import { paths } from "./paths";

export interface LoginLocationState {
  from?: string;
}

/**
 * Exige sesión (`GET /v1/me`); sin ella, a `/login` recordando a dónde iba. Da
 * la sesión a todo lo de debajo por contexto (`useSession`).
 */
export const RequireSession = () => {
  const { data: me, isPending, isError, refetch } = useMe();
  const location = useLocation();

  if (isPending) return <FullScreenLoader />;
  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <ErrorState onRetry={() => void refetch()} />
      </div>
    );
  }
  if (!me) {
    const state: LoginLocationState = {
      from: `${location.pathname}${location.search}`,
    };
    return <Navigate to={paths.login} replace state={state} />;
  }
  return (
    <SessionProvider me={me}>
      <Outlet />
    </SessionProvider>
  );
};

/** Solo para `/login`: con sesión no tiene sentido verlo. */
export const RedirectIfSession = () => {
  const { data: me, isPending } = useMe();
  const location = useLocation();
  const from = (location.state as LoginLocationState | null)?.from;

  if (isPending) return <FullScreenLoader />;
  if (me) return <Navigate to={from ?? paths.home} replace />;
  return <Outlet />;
};

/** Exige una organización elegida; si no, al selector (que decide qué hacer). */
export const RequireOrganization = () => {
  const { currentOrganization } = useSession();
  return currentOrganization ? (
    <Outlet />
  ) : (
    <Navigate to={paths.selectOrganization} replace />
  );
};

/** Exige un nivel de acceso (`admin`, `platform`…); si no, «Sin acceso». */
export const RequireAccess = ({ access }: { access: Access }) => {
  const session = useSession();
  return canAccess(session, access) ? <Outlet /> : <ForbiddenPage />;
};

/**
 * `/`: Inicio es del administrador. Un usuario no tiene Inicio (la UX solo le
 * da «Mis certificados» y «Mi actividad»), así que va a sus certificados.
 */
export const HomeRoute = () => {
  const { isAdmin } = useSession();
  return isAdmin ? <Outlet /> : <Navigate to={paths.myCertificates} replace />;
};
